import React, { useEffect, useRef, useState } from 'react';
import { Volume2, VolumeX, Play } from 'lucide-react';
import { BACKEND_URL, imgUrl } from './promoCart';

const WIDTHS = [480, 720, 1080, 1440, 2160];
export const MEDIA_BG = 'bg-[radial-gradient(ellipse_at_center,#1a1a1c_0%,#0A0A0A_70%)]';
const isOwnImage = (u) => u && u.startsWith('/api/promotions/files/');

export const webpSrc = (u, w) => (isOwnImage(u) ? `${BACKEND_URL}/api/promo-media/image?src=${encodeURIComponent(u)}&w=${w}` : imgUrl(u));
export const webpSrcSet = (u) => (isOwnImage(u) ? WIDTHS.map((w) => `${webpSrc(u, w)} ${w}w`).join(', ') : undefined);

export const ResponsiveImg = ({ src, alt, className, sizes = '(max-width: 768px) 100vw, 42vw', eager, testid }) => (
  <img src={webpSrc(src, 1080)} srcSet={webpSrcSet(src)} sizes={sizes} alt={alt} className={className}
    loading={eager ? 'eager' : 'lazy'} decoding="async" fetchPriority={eager ? 'high' : undefined} data-testid={testid} />
);

const useInView = (ref, threshold = 0.35) => {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || !('IntersectionObserver' in window)) { setInView(true); return undefined; }
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold });
    io.observe(el);
    return () => io.disconnect();
  }, [ref, threshold]);
  return inView;
};

const useAfterLoad = (delay) => {
  const [ok, setOk] = useState(false);
  useEffect(() => {
    let t;
    const go = () => { t = setTimeout(() => setOk(true), delay); };
    if (document.readyState === 'complete') go(); else window.addEventListener('load', go, { once: true });
    return () => { clearTimeout(t); window.removeEventListener('load', go); };
  }, [delay]);
  return ok;
};

const OfferVideo = ({ offer, eager }) => {
  const box = useRef(null);
  const video = useRef(null);
  const inView = useInView(box);
  const poster = offer.video_poster || offer.images?.[0];
  const pageReady = useAfterLoad(eager && poster ? 2000 : 0);
  const [started, setStarted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);

  useEffect(() => { if (inView && pageReady) setStarted(true); }, [inView, pageReady]);
  useEffect(() => {
    const v = video.current;
    if (!v || !started) return;
    v.muted = true;
    v.setAttribute('muted', '');
    v.setAttribute('playsinline', '');
    v.setAttribute('webkit-playsinline', '');
  }, [started]);
  useEffect(() => {
    const v = video.current;
    if (!v || !started) return;
    if (inView) v.play().catch(() => {}); else v.pause();
  }, [inView, started]);

  const toggleSound = () => {
    const v = video.current;
    if (!v) return;
    v.muted = !muted;
    setMuted(!muted);
    if (v.paused) v.play().catch(() => {});
  };

  return (
    <div ref={box} className="relative w-full h-full bg-black" data-testid={`offer-video-box-${offer.slug}`}>
      {poster && !playing && <ResponsiveImg src={poster} alt={offer.name} eager={eager} className="absolute inset-0 w-full h-full object-cover" testid={`offer-video-poster-${offer.slug}`} />}
      {started && (
        <video ref={video} src={imgUrl(offer.video_url)} onPlaying={() => setPlaying(true)}
          muted loop playsInline autoPlay preload="metadata" className={`absolute inset-0 w-full h-full object-cover ${playing ? '' : 'opacity-0'}`} data-testid={`offer-video-${offer.slug}`} />
      )}
      <button type="button" onClick={toggleSound} aria-label={muted ? 'Activer le son' : 'Couper le son'}
        className="absolute bottom-3 right-3 h-11 px-4 rounded-full bg-black/65 backdrop-blur-md text-[#F2EFE8] text-sm flex items-center gap-2 border border-white/10 hover:bg-black/80 transition-colors" data-testid={`offer-video-sound-${offer.slug}`}>
        {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />} {muted ? 'Activer le son' : 'Son activé'}
      </button>
    </div>
  );
};

export const parseEmbed = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  if (yt) return { src: `https://www.youtube-nocookie.com/embed/${yt[1]}?autoplay=1&mute=1&loop=1&playlist=${yt[1]}&playsinline=1&rel=0&modestbranding=1`, thumb: `https://img.youtube.com/vi/${yt[1]}/hqdefault.jpg` };
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return { src: `https://player.vimeo.com/video/${vm[1]}?autoplay=1&muted=1&loop=1&playsinline=1&title=0&byline=0`, thumb: null };
  return null;
};

const OfferEmbed = ({ offer, eager }) => {
  const box = useRef(null);
  const inView = useInView(box, 0.2);
  const [started, setStarted] = useState(false);
  const embed = parseEmbed(offer.video_embed_url);
  useEffect(() => { if (inView) setStarted(true); }, [inView]);
  if (!embed) return null;
  const poster = offer.video_poster || offer.images?.[0];
  return (
    <div ref={box} className="relative w-full h-full bg-black" data-testid={`offer-embed-box-${offer.slug}`}>
      {!started && (poster ? <ResponsiveImg src={poster} alt={offer.name} eager={eager} className="absolute inset-0 w-full h-full object-cover" />
        : embed.thumb && <img src={embed.thumb} alt="" className="absolute inset-0 w-full h-full object-cover" />)}
      {!started && <Play className="absolute inset-0 m-auto w-14 h-14 text-white/80" />}
      {started && <iframe src={embed.src} title={offer.name} allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen
        className="absolute inset-0 w-full h-full" data-testid={`offer-embed-${offer.slug}`} />}
    </div>
  );
};

const useSwipe = (count, setIdx) => {
  const x0 = useRef(null);
  return {
    onTouchStart: (e) => { x0.current = e.touches[0].clientX; },
    onTouchEnd: (e) => {
      if (x0.current == null) return;
      const dx = e.changedTouches[0].clientX - x0.current;
      x0.current = null;
      if (Math.abs(dx) > 40) setIdx((i) => (i + (dx < 0 ? 1 : -1) + count) % count);
    },
  };
};

export const Thumbs = ({ offer, items, idx, setIdx }) => (
  <div className="flex gap-2 px-3 pt-3 overflow-x-auto" data-testid={`offer-thumbs-${offer.slug}`}>
    {items.map((it, i) => (
      <button key={it.key} type="button" onClick={() => setIdx(i)} aria-label={it.type === 'video' ? 'Vidéo' : `Photo ${i + 1}`}
        className={`relative shrink-0 w-14 h-14 rounded-xl overflow-hidden border-2 bg-[#0A0A0A] transition-colors ${i === idx ? 'border-[#C9A84C]' : 'border-white/10 opacity-70 hover:opacity-100'}`}
        data-testid={`offer-thumb-${offer.slug}-${i}`}>
        {it.type === 'video'
          ? <>{it.poster ? <img src={webpSrc(it.poster, 320)} alt="" className="w-full h-full object-cover" /> : <span className="block w-full h-full bg-zinc-800" />}
            <Play className="absolute inset-0 m-auto w-5 h-5 text-white drop-shadow" /></>
          : <img src={webpSrc(it.src, 320)} alt="" loading="lazy" className="w-full h-full object-contain" />}
      </button>
    ))}
  </div>
);

const OfferGallery = ({ offer, eager }) => {
  const [idx, setIdx] = useState(0);
  const images = offer.images || [];
  const swipe = useSwipe(images.length, setIdx);
  if (!images.length) return <div className={`w-full h-full flex items-center justify-center ${MEDIA_BG} text-zinc-500 text-sm`} data-testid={`offer-no-image-${offer.slug}`}>Photo à venir</div>;
  return (
    <div className={`relative w-full h-full ${MEDIA_BG}`} {...(images.length > 1 ? swipe : {})} data-testid={`offer-gallery-${offer.slug}`}>
      <ResponsiveImg key={images[idx]} src={images[idx]} alt={offer.name} eager={eager && idx === 0}
        className="absolute inset-0 w-full h-full object-contain p-3 animate-[promoFade_.35s_ease-out]" testid={`offer-image-${offer.slug}`} />
    </div>
  );
};

export const galleryItems = (offer) => {
  const images = offer.images || [];
  if ((offer.media_type === 'video_images' || offer.media_type === 'video') && offer.video_url && (offer.media_type === 'video_images' || (offer.images || []).length)) {
    const video = { type: 'video', key: 'video', poster: offer.video_poster || images[0] };
    const photos = images.map((src) => ({ type: 'image', key: src, src }));
    return offer.media_first === 'images' ? [...photos, video] : [video, ...photos];
  }
  if (!offer.media_type || offer.media_type === 'images') return images.map((src) => ({ type: 'image', key: src, src }));
  return [];
};

const MixedGallery = ({ offer, eager, idx, setIdx }) => {
  const items = galleryItems(offer);
  const swipe = useSwipe(items.length, setIdx);
  const cur = items[idx] || items[0];
  return (
    <div className={`relative w-full h-full ${MEDIA_BG}`} {...swipe} data-testid={`offer-mixed-${offer.slug}`}>
      {cur.type === 'video' ? <OfferVideo offer={offer} eager={eager} /> : (
        <ResponsiveImg key={cur.src} src={cur.src} alt={offer.name} eager={eager && idx === 0}
          className="absolute inset-0 w-full h-full object-contain p-3 animate-[promoFade_.35s_ease-out]" testid={`offer-image-${offer.slug}`} />
      )}
    </div>
  );
};

const ImageStage = ({ offer, eager, idx, setIdx }) => {
  const items = galleryItems(offer);
  const swipe = useSwipe(items.length, setIdx);
  const src = items[idx]?.src || items[0]?.src;
  if (!src) return <OfferGallery offer={offer} eager={eager} />;
  return (
    <div className={`relative w-full h-full ${MEDIA_BG}`} {...(items.length > 1 ? swipe : {})} data-testid={`offer-gallery-${offer.slug}`}>
      <ResponsiveImg key={src} src={src} alt={offer.name} eager={eager && idx === 0}
        className="absolute inset-0 w-full h-full object-contain p-3 animate-[promoFade_.35s_ease-out]" testid={`offer-image-${offer.slug}`} />
    </div>
  );
};

export const OfferMedia = ({ offer, eager, idx = 0, setIdx = () => {} }) => {
  if ((offer.media_type === 'video_images' || offer.media_type === 'video') && offer.video_url && (offer.media_type === 'video_images' || (offer.images || []).length)) {
    return (offer.images || []).length ? <MixedGallery offer={offer} eager={eager} idx={idx} setIdx={setIdx} /> : <OfferVideo offer={offer} eager={eager} />;
  }
  if (offer.media_type === 'video' && offer.video_url) return <OfferVideo offer={offer} eager={eager} />;
  if (offer.media_type === 'embed' && parseEmbed(offer.video_embed_url)) return <OfferEmbed offer={offer} eager={eager} />;
  return <ImageStage offer={offer} eager={eager} idx={idx} setIdx={setIdx} />;
};
